import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import Logo from '../components/Logo';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';
import Seo from '../components/Seo';

const NAVY = '#0A1F44';
const GOLD = '#C0975C';
const IVORY = '#F5F1E8';
const serif = { fontFamily: "'Outfit', Georgia, serif" };
const inputBox = { border: '1px solid rgba(10,31,68,0.18)', color: NAVY };

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'signup');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({ username: '', email: '', password: '', age: '' });

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password, rememberMe);
        toast.success('Welcome back to Precision Wealth.');
      } else {
        if (!formData.age || parseInt(formData.age) < 18) {
          toast.error('You must be 18 or older to register.');
          setLoading(false);
          return;
        }
        await register(formData.username, formData.email, formData.password, formData.age, rememberMe);
        trackEvent('sign_up', { method: 'email' });
        toast.success('Welcome to Precision Wealth. Your journey starts now.');
      }
      navigate('/dashboard');
    } catch (error) {
      let message = 'Something went wrong';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        message = detail.map((err) => err.msg || err.message || 'Validation error').join(', ');
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: IVORY, fontFamily: "'Inter', sans-serif" }}>
      <Seo
        title="Sign in or create your account — Precision Wealth"
        description="Create your free Precision Wealth account to access financial courses, quizzes and calculators built for South Africans."
        path="/auth"
        noindex
      />
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12">
        <Link to="/" className="inline-flex items-center gap-2 mb-12 w-fit text-sm" style={{ color: '#4a5568' }}>
          <ArrowLeft className="w-4 h-4" />
          <span>Back to home</span>
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="mb-10">
            <Logo variant="dark" size="md" layout="horizontal" />
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ ...serif, color: NAVY }}>
            {isLogin ? 'Welcome back' : 'Create your account'}
          </h1>
          <p className="mb-8 text-[15px]" style={{ color: '#4a5568' }}>
            {isLogin
              ? 'Sign in to continue your financial education.'
              : 'Full access to courses, tools and your Precision Assistant.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <label className="block text-xs mb-2 uppercase tracking-wider" style={{ color: NAVY }}>Username</label>
                <input
                  type="text" name="username" value={formData.username} onChange={handleChange}
                  className="w-full px-4 py-3 outline-none text-sm bg-white" style={inputBox}
                  placeholder="Choose a username" required={!isLogin} data-testid="username-input"
                />
              </div>
            )}

            <div>
              <label className="block text-xs mb-2 uppercase tracking-wider" style={{ color: NAVY }}>Email</label>
              <input
                type="email" name="email" value={formData.email} onChange={handleChange}
                className="w-full px-4 py-3 outline-none text-sm bg-white" style={inputBox}
                placeholder="your@email.com" required data-testid="email-input"
              />
            </div>

            <div className="relative">
              <label className="block text-xs mb-2 uppercase tracking-wider" style={{ color: NAVY }}>Password</label>
              <input
                type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange}
                className="w-full px-4 py-3 pr-12 outline-none text-sm bg-white" style={inputBox}
                placeholder="••••••••" required minLength={6} data-testid="password-input"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 bottom-3" style={{ color: '#4a5568' }}>
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {!isLogin && (
              <div>
                <label className="block text-xs mb-2 uppercase tracking-wider" style={{ color: NAVY }}>Age</label>
                <input
                  type="number" name="age" value={formData.age} onChange={handleChange}
                  className="w-full px-4 py-3 outline-none text-sm bg-white" style={inputBox}
                  placeholder="Your age" min="18" max="120" required={!isLogin} data-testid="age-input"
                />
              </div>
            )}

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox" id="rememberMe" checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 cursor-pointer" style={{ accentColor: GOLD }}
                />
                <label htmlFor="rememberMe" className="text-sm cursor-pointer select-none" style={{ color: '#4a5568' }}>
                  Keep me signed in
                </label>
              </div>
              {isLogin && (
                <Link to="/forgot-password" className="text-sm font-semibold" style={{ color: NAVY }} data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              )}
            </div>

            <button
              type="submit" disabled={loading}
              className="w-full py-4 font-medium transition-all hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: GOLD, color: NAVY }} data-testid="submit-button"
            >
              {loading ? 'Please wait…' : isLogin ? 'Sign In' : 'Create Account'}
            </button>
            {!isLogin && (
              <p className="text-xs text-center leading-relaxed" style={{ color: '#7a8699' }} data-testid="signup-privacy-notice">
                By creating an account you agree to our <a href="/terms" className="underline" style={{ color: NAVY }}>Terms</a> and confirm you are 18 or older. We process your details under POPIA as set out in our <a href="/privacy" className="underline" style={{ color: NAVY }}>Privacy Notice</a>.
              </p>
            )}
          </form>

          <div className="mt-8 text-center">
            <button onClick={() => setIsLogin(!isLogin)} className="text-sm" style={{ color: '#4a5568' }} data-testid="toggle-auth-mode">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <span className="font-semibold" style={{ color: NAVY }}>{isLogin ? 'Sign Up' : 'Sign In'}</span>
            </button>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: NAVY }}>
        <div className="absolute inset-0" style={{
          backgroundImage: 'url(/brand/hero-brand.webp)',
          backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.35,
        }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(130deg, ${NAVY} 35%, rgba(10,31,68,0.55) 65%, rgba(201,169,97,0.40) 100%)` }} />

        <div className="relative z-10 flex flex-col justify-center px-16">
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-8" style={{ border: `1px solid ${GOLD}`, color: GOLD }}>
              <span className="text-xs tracking-[0.25em] uppercase">Find the extraordinary</span>
            </div>
            <h2 className="text-4xl font-bold mb-6" style={{ ...serif, color: IVORY }}>
              Knowledge is the first form of <span style={{ color: GOLD }}>wealth.</span>
            </h2>
            <p className="text-lg mb-8" style={{ color: '#c6d0e0' }}>
              Financial and business education for individuals and businesses across South Africa.
            </p>

            <div className="space-y-4">
              {['Master budgeting, investing, tax & estates', 'Model your financial future with real tools', 'Speak to an adviser when you are ready'].map((t, i) => (
                <div key={i} className="flex items-center gap-4" style={{ color: IVORY }}>
                  <div className="w-9 h-9 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: GOLD, color: NAVY }}>
                    <span className="font-bold">{i + 1}</span>
                  </div>
                  <span className="text-sm">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
